import { Link, useLocation } from "wouter";
import { useAuth } from "@/lib/auth-context";
import { useTheme } from "@/lib/theme-context";
import { 
  BookOpen, 
  LayoutDashboard, 
  PlusCircle, 
  History, 
  Settings, 
  LogOut,
  Menu,
  X,
  Sun,
  Moon,
  Sparkles
} from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export function AppLayout({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [location] = useLocation();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isSignOutOpen, setIsSignOutOpen] = useState(false);

  const navItems = [
    { href: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { href: "/sessions/new", label: "New Session", icon: PlusCircle },
    { href: "/history", label: "History", icon: History },
    { href: "/space", label: "Space", icon: Sparkles },
    { href: "/settings", label: "Settings", icon: Settings },
  ];

  const SidebarContent = () => (
    <>
      <div className="flex items-center gap-3 px-2 mb-10">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center shadow-lg shadow-primary/20">
          <BookOpen className="w-5 h-5 text-white" />
        </div>
        <span className="font-display font-bold text-xl tracking-tight text-foreground">Lerni</span>
      </div>

      <nav className="flex-1 space-y-2">
        {navItems.map((item) => {
          const isActive = location === item.href || (item.href !== "/dashboard" && location.startsWith(item.href));
          const Icon = item.icon;
          return (
            <Link key={item.href} href={item.href} onClick={() => setIsMobileOpen(false)}>
              <div className={cn(
                "flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all cursor-pointer",
                isActive 
                  ? "bg-primary text-primary-foreground shadow-md shadow-primary/20" 
                  : "text-muted-foreground hover:bg-primary/5 hover:text-primary"
              )}>
                <Icon className={cn("w-5 h-5", isActive ? "text-primary-foreground" : "opacity-70")} />
                {item.label}
              </div>
            </Link>
          );
        })}
      </nav>

      <div className="pt-8 mt-8 border-t border-border space-y-3">
        <button
          onClick={toggleTheme}
          className="flex w-full items-center gap-3 px-4 py-3 rounded-xl font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
        >
          {theme === "dark" ? (
            <Sun className="w-5 h-5 opacity-70" />
          ) : (
            <Moon className="w-5 h-5 opacity-70" />
          )}
          {theme === "dark" ? "Light Mode" : "Dark Mode"}
        </button>
        <div className="px-4 mb-2">
          <p className="text-sm font-medium text-foreground truncate">{user?.name}</p>
          <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
        </div>
        <button
          onClick={() => {
            setIsMobileOpen(false);
            setIsSignOutOpen(true);
          }}
          className="flex w-full items-center gap-3 px-4 py-3 rounded-xl font-medium text-destructive hover:bg-destructive/10 transition-colors"
        >
          <LogOut className="w-5 h-5 opacity-70" />
          Sign Out
        </button>
      </div>

      <AlertDialog open={isSignOutOpen} onOpenChange={setIsSignOutOpen}>
        <AlertDialogContent className="max-w-md rounded-2xl border-border bg-card p-6 shadow-2xl">
          <AlertDialogHeader className="space-y-3 text-left">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-destructive/10 text-destructive">
              <LogOut className="h-5 w-5" />
            </div>
            <div className="space-y-1.5">
              <AlertDialogTitle className="font-display text-xl">Sign out of Lerni?</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to sign out? You’ll need to sign in again to access your study sessions.
              </AlertDialogDescription>
            </div>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-2">
            <AlertDialogCancel className="mt-0 rounded-xl">Keep me signed in</AlertDialogCancel>
            <AlertDialogAction
              className="rounded-xl bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={logout}
            >
              Sign Out
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex w-72 flex-col fixed inset-y-0 left-0 bg-card border-r border-border p-6 z-20">
        <SidebarContent />
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-16 bg-card/80 backdrop-blur-md border-b border-border z-30 flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center">
            <BookOpen className="w-4 h-4 text-white" />
          </div>
          <span className="font-display font-bold text-lg text-foreground">Lerni</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={toggleTheme} aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"} className="p-2 text-muted-foreground hover:text-foreground transition-colors">
            {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>
          <button onClick={() => setIsMobileOpen(!isMobileOpen)} className="p-2 -mr-2 text-foreground">
            {isMobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {isMobileOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-background/80 backdrop-blur-sm" onClick={() => setIsMobileOpen(false)}>
          <div className="absolute inset-y-0 right-0 w-3/4 max-w-sm bg-card border-l border-border p-6 flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex justify-end mb-6">
              <button onClick={() => setIsMobileOpen(false)} className="p-2 -mr-2 text-muted-foreground">
                <X className="w-6 h-6" />
              </button>
            </div>
            <SidebarContent />
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 md:ml-72 pt-16 md:pt-0 min-h-screen relative flex flex-col">
        {location === "/space" ? (
          <div className="flex-1 min-h-0 h-[calc(100vh-4rem)] md:h-screen">
            {children}
          </div>
        ) : (
          <>
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-[100px] -z-10 pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-accent/5 rounded-full blur-[100px] -z-10 pointer-events-none" />
            <div className="p-4 sm:p-8 lg:p-10 max-w-7xl mx-auto flex-1">
              {children}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
